import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Pressable, ViewStyle } from 'react-native'
import { OverlayProvider, Tooltip as RNTooltip } from 'react-native-popper'

import Constants from 'expo-constants'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { isChromaticWeb, tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'
import { InfoIcon } from '../icons/InfoIcon'
import { QuestionMarkIcon } from '../icons/QuestionMarkIcon'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'

export interface TooltipProps {
  placement?: TooltipPlacement
  testID?: string
  animationExitDuration?: number
  content?: string | React.ReactNode
  contentStyle?: { container?: Dict }
  children?: ReactNode
  triggerConfig?: ToolTipTriggerConfig
  offset?: number
  crossOffset?: number
  backgroundColor?: string
  contentTextColor?: string
  contentTextSize?: BodyTextSize
  showArrow?: boolean
  style?: ViewStyle
  hoverEnabled?: boolean
  accessibilityLabel?: string
  onPress?: () => void
  wrapperStyle?: ViewStyle
  tooltipSize?: TooltipMaxWidth
  hideTooltip?: boolean
  asChild?: boolean
}

type TooltipManager = {
  show: () => void
  hide: () => void
}

export const TooltipProvider = OverlayProvider

export type TooltipPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top left'
  | 'top right'
  | 'bottom left'
  | 'bottom right'

const Wrapper = styled(Flex1View)({
  flexDirection: 'row',
  alignItems: 'center',
})

const ContentContainer = styled(PressableOpacity)<{
  theme: ThemeType
  backgroundColor?: string
  tooltipSize?: TooltipMaxWidth
  styles?: { [key: string]: Dict }
}>(({ theme, backgroundColor, tooltipSize = TooltipMaxWidth.DEFAULT, styles = {} }) => ({
  maxWidth: tooltipSize,
  zIndex: 1,
  paddingVertical: 8,
  paddingHorizontal: 12,
  borderRadius: 4,
  backgroundColor: backgroundColor || theme.colors.backgroundTooltip,
  ...(styles && { ...styles.container }),
}))

export enum TooltipMaxWidth {
  DEFAULT = 224,
  WIDE = 335,
}

export enum TOOLTIP_TRIGGER_ICON {
  QUESTION_MARK = 'questionMark',
  INFO = 'info',
}

/**
 * The tooltip trigger defaults to the QuestionMarkIcon (filled).
 */
export interface ToolTipTriggerConfig {
  icon?: TOOLTIP_TRIGGER_ICON
  isFilled?: boolean
  fillColor?: string
  size?: number
}

export const Tooltip = forwardRef<TooltipManager, TooltipProps>(
  (
    {
      children,
      animationExitDuration = isChromaticWeb() ? 0 : 500,
      placement = 'bottom',
      testID,
      content,
      triggerConfig,
      offset,
      crossOffset,
      hoverEnabled,
      contentStyle,
      backgroundColor,
      contentTextColor,
      contentTextSize = BodyTextSize.SMALL,
      showArrow = true,
      style,
      accessibilityLabel,
      onPress = () => {},
      wrapperStyle,
      tooltipSize = TooltipMaxWidth.DEFAULT,
      hideTooltip = false,
      asChild = false,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const { formatMessage } = useIntl()
    const { colors } = useTheme()

    const computedBg = backgroundColor ?? colors.backgroundTooltip
    const computedTxtColor = contentTextColor ?? colors.components?.tooltip?.text ?? colors.textInverse

    const tooltipContentRef = useRef<any>(null)
    const triggerWrapperRef = useRef<any>(null)

    const isChildrenString = typeof children === 'string'
    const isFilled = triggerConfig?.isFilled ?? true
    const size = triggerConfig?.size || 16
    const fillColor = triggerConfig?.fillColor || colors.iconActive

    let trigger = <QuestionMarkIcon width={size} isFilled />
    if (!isChildrenString && children) {
      trigger = React.cloneElement(children as any)
    } else if (triggerConfig?.icon === TOOLTIP_TRIGGER_ICON.QUESTION_MARK) {
      trigger = <QuestionMarkIcon width={size} isFilled={isFilled} fillColor={fillColor} />
    } else if (triggerConfig?.icon === TOOLTIP_TRIGGER_ICON.INFO) {
      trigger = <InfoIcon size={size} fillColor={fillColor} />
    }

    // Check if the content prop is of type string or FormattedMessage to determine if we should render
    // the tooltip content as a BodyText component with font and text styling, or as a React Node child
    const isContentString = typeof content === 'string'
    const isContentFormattedMessage =
      content && !isContentString && (content as React.ReactElement).type === FormattedMessage

    /**
     * ⚠️ There is a bug with storybook-native rendering the view within a SafeAreaView
     * which messes up the detection of the tooltip. So we will offset the tooltip
     * by hardcoding it to your device. If you are testing tooltips with a device that has notches
     * that is not an iPhone 12 pro, make sure to offset the tooltip below
     * with the value that the top of the SafeArea inset and bottom of the storybook story
     * https://github.com/storybookjs/react-native/pull/345
     */
    const getOffset = () => {
      if (Constants?.expoConfig?.extra?.IS_STORYBOOK) {
        if (placement.includes('top')) {
          return -35
        }
        if (placement.includes('bottom')) {
          return -47
        }
      }
      return offset
    }

    /**
     * If user passes a ref to the Tooltip, the user
     * can control the tooltip open and closing by ref.current.show() / ref.current.hide()
     */
    useImperativeHandle(ref, () => ({
      show: () => setOpen(true),
      hide: () => setOpen(false),
    }))

    // This fixes a bug where a tooltip were not able to show on top of a Modal on Web.
    // By default The z-index of the parent element of the Tooltip content is `0`,
    // which causes it to be behind the modal. Replacing the z-index by 'auto' fixes the issue.
    useEffect(() => {
      if (open && IS_WEB) {
        const tooltipContentParent = tooltipContentRef?.current?.parentNode?.parentNode?.parentNode
        if (tooltipContentParent) {
          tooltipContentParent.style.zIndex = 'auto'
        }
      }
    }, [open])

    // Dismiss a press-triggered tooltip when clicking outside of it. react-native-popper's
    // Tooltip omits the trigger/overlay refs from its outside-click handler (unlike Popover),
    // so on web it can't distinguish inside vs outside clicks. We handle it here against the
    // refs we control. Native uses the Backdrop, and hover tooltips close on hover-out.
    useEffect(() => {
      if (!IS_WEB || hoverEnabled || !open) {
        return
      }
      const handleDocumentClick = (event: MouseEvent) => {
        const target = event.target as Node | null
        const clickedTrigger = !!target && !!triggerWrapperRef.current?.contains?.(target)
        const clickedContent = !!target && !!tooltipContentRef.current?.contains?.(target)
        if (!clickedTrigger && !clickedContent) {
          setOpen(false)
        }
      }
      document.addEventListener('click', handleDocumentClick)
      return () => document.removeEventListener('click', handleDocumentClick)
    }, [open, hoverEnabled])

    const tooltipTestID = tID(`Tooltip${testID ? `-${testID}` : ''}`)
    const tooltipA11yLabel =
      accessibilityLabel ?? formatMessage({ defaultMessage: 'Tooltip', description: 'Tooltip for more information' })

    if (hideTooltip) {
      if (asChild) {
        const child = React.Children.only(children) as React.ReactElement<any>
        return (
          <Wrapper style={wrapperStyle}>
            {React.cloneElement(child, {
              accessibilityLabel: child.props.accessibilityLabel ?? tooltipA11yLabel,
              testID: child.props.testID ?? tooltipTestID,
              onPress: () => {
                child.props.onPress?.()
                onPress()
              },
            })}
          </Wrapper>
        )
      }
      return (
        <Wrapper style={wrapperStyle}>
          <Pressable accessibilityLabel={accessibilityLabel} testID={tooltipTestID} onPress={onPress} style={style}>
            {trigger}
          </Pressable>
        </Wrapper>
      )
    }

    let triggerElement: React.ReactElement
    if (asChild) {
      const child = React.Children.only(children) as React.ReactElement<any>
      triggerElement = React.cloneElement(child, {
        accessibilityLabel: child.props.accessibilityLabel ?? tooltipA11yLabel,
        hitSlop: child.props.hitSlop ?? 24,
        testID: child.props.testID ?? tooltipTestID,
        onPress: hoverEnabled
          ? () => {
              child.props.onPress?.()
              onPress()
            }
          : () => {
              setOpen(!open)
              child.props.onPress?.()
              onPress()
            },
        onHoverIn: hoverEnabled
          ? () => {
              child.props.onHoverIn?.()
              setOpen(true)
            }
          : child.props.onHoverIn,
        onHoverOut: hoverEnabled
          ? () => {
              child.props.onHoverOut?.()
              setOpen(false)
            }
          : child.props.onHoverOut,
        onFocus: hoverEnabled
          ? () => {
              child.props.onFocus?.()
              setOpen(true)
            }
          : child.props.onFocus,
        onBlur: hoverEnabled
          ? () => {
              child.props.onBlur?.()
              setOpen(false)
            }
          : child.props.onBlur,
      })
    } else {
      triggerElement = (
        <Pressable
          accessibilityLabel={tooltipA11yLabel}
          hitSlop={24}
          testID={tooltipTestID}
          onPress={
            hoverEnabled
              ? onPress
              : () => {
                  setOpen(!open)
                  onPress()
                }
          }
          onHoverIn={hoverEnabled ? () => setOpen(true) : noop}
          onHoverOut={hoverEnabled ? () => setOpen(false) : noop}
          onFocus={hoverEnabled ? () => setOpen(true) : noop}
          onBlur={hoverEnabled ? () => setOpen(false) : noop}
          style={style}
        >
          {trigger}
        </Pressable>
      )
    }

    return (
      <Wrapper style={wrapperStyle} ref={triggerWrapperRef}>
        <RNTooltip
          isOpen={open}
          on='press'
          mode='multiple'
          placement={placement}
          offset={getOffset()}
          crossOffset={crossOffset}
          animationExitDuration={animationExitDuration}
          trigger={triggerElement}
        >
          {!hoverEnabled && <RNTooltip.Backdrop onPress={() => setOpen(false)} />}
          <RNTooltip.Content>
            {showArrow && <RNTooltip.Arrow height={10} width={10} style={{ backgroundColor: computedBg }} />}
            <ContentContainer
              /* existing ref for z-index tweak */
              pressableRef={tooltipContentRef}
              /* pass tooltipSize to control maxWidth */
              tooltipSize={tooltipSize}
              styles={contentStyle}
              testID={tID(`Tooltip-content${testID ? `-${testID}` : ''}`)}
              backgroundColor={computedBg}
              onPress={hoverEnabled ? noop : () => setOpen(false)}
              onHoverIn={hoverEnabled ? () => setOpen(true) : noop}
              onHoverOut={hoverEnabled ? () => setOpen(false) : noop}
            >
              {isContentString || isContentFormattedMessage ? (
                <BodyText text={content as string} size={contentTextSize} color={computedTxtColor} />
              ) : (
                content
              )}
            </ContentContainer>
          </RNTooltip.Content>
        </RNTooltip>
      </Wrapper>
    )
  },
)
